// useAccountAdminAccount.js (loop-safe)
import { useState, useEffect, useContext, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import { logError } from "util/logging";
import { strNonEmpty } from "util/strings";
import { ModalContext } from "contexts/Modal";

const emptyInputs = { name: "", brand: "" };

const freshSession = async () => {
  await Auth.currentAuthenticatedUser({ bypassCache: true });
  return Auth.currentSession();
};

const useAccountAdminAccount = (accountId, account) => {
  const [inputs, setInputs] = useState(emptyInputs);
  const [availableBrands, setBrands] = useState([]);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);

  const { showModal } = useContext(ModalContext);

  const isMounted = useRef(false);
  const fetchedBrandsOnce = useRef(false);      // fetch only once per auth session
  const fetchInFlight = useRef(false);
  const nextAllowedFetchAt = useRef(0);         // lockout window after error (ms since epoch)

  // --- Auth readiness (no tokenRefresh noise) ---
  useEffect(() => {
    isMounted.current = true;

    (async () => {
      try {
        await freshSession();
        if (isMounted.current) setReady(true);
      } catch {
        if (isMounted.current) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await freshSession();
          if (isMounted.current) {
            setReady(true);
            fetchedBrandsOnce.current = false;  // new session ⇒ allow one fetch
          }
        } catch {
          if (isMounted.current) setReady(false);
        }
      } else if (evt === "signOut") {
        if (isMounted.current) {
          setReady(false);
          setBrands([]);
          setInputs(emptyInputs);
          setSaving(false);
          fetchedBrandsOnce.current = false;    // reset for next session
        }
      }
      // IMPORTANT: deliberately ignore tokenRefresh & tokenRefresh_failure
    });

    return () => { isMounted.current = false; unsub(); };
  }, []);

  // Keep inputs in sync with current account
  useEffect(() => {
    if (account?.id) {
      setInputs({
        ...account,
        brand: account.brand?.name ?? "",
      });
    } else {
      setInputs(emptyInputs);
    }
  }, [accountId, account]);

  // --- Fetch brands exactly once after ready=true (per session) ---
  useEffect(() => {
    if (!ready) return;
    if (fetchedBrandsOnce.current) return;

    const now = Date.now();
    if (now < nextAllowedFetchAt.current) return; // cooldown after error
    if (fetchInFlight.current) return;

    fetchInFlight.current = true;
    let cancelled = false;

    (async () => {
      try {
        const session = await freshSession();

        // If your API expects a Bearer token, keep this header.
        // If your API uses cookies only, remove the headers block and keep withCredentials.
        const token = session.getIdToken().getJwtToken();

        const brands = await API.get("AccountGatewayApi", "/brands");

        if (!cancelled && isMounted.current) {
          setBrands((brands || []).map((b) => b.name));
          fetchedBrandsOnce.current = true;    // success ⇒ don’t refetch this session
        }
      } catch (err) {
        // Back off for 5 seconds to avoid loops if backend returns 4xx without CORS
        nextAllowedFetchAt.current = Date.now() + 5000;
        const status = err?.response?.status;
        const data = err?.response?.data;
        console.error("Brands fetch failed", { status, data, err });
      } finally {
        fetchInFlight.current = false;
      }
    })();

    return () => { cancelled = true; };
  }, [ready]);

  const handleSave = async (event) => {
    if (event) event.preventDefault();
    if (saving) return;
    setSaving(true);

    try {
      if (!strNonEmpty(inputs.name)) {
        showModal("Save Account", "Name cannot be empty.");
        setSaving(false);
        return;
      }

      const session = await freshSession().catch(() => null);
      if (!session) {
        showModal("Save Account", "You're not signed in yet. Please sign in and try again.");
        setSaving(false);
        return;
      }

      const token = session.getIdToken().getJwtToken();

      await API.patch("AccountGatewayApi", `/account/${accountId}`, {
        body: { name: inputs.name, brand: inputs.brand }
      });

      if (isMounted.current) {
        setSaving(false);
        showModal("Account Save", "Account successfully updated.");
      }
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (isMounted.current) {
        setSaving(false);
        logError("AccountAdminAccount save error", { status, data, err });
        showModal(
          "Account save unsuccessful",
          status ? `Server responded ${status}.` : "Error occurred while saving account."
        );
      }
    }
  };

  return { inputs, setInputs, handleSave, availableBrands, saving };
};

export default useAccountAdminAccount;
